import { FulfillmentParameters, UserDetails, OfferingDetails, SubmitFormPayload, FormConfigDetails } from "../models/mercury-form";

// Where a formConfigDetails row sends "Get Started". FILM's Form Manager
// writes the Platform dropdown ('Service Now' | 'Mercury' | 'URL') into
// fulfillmentChannel, and formId holds the SysID / Mercury form ID / URL.
export type FulfillmentChannelKind = 'servicenow' | 'mercury' | 'url' | 'unknown';

export class FulfillmentUtils {

  // PEGA has sent both "Service Now" and "ServiceNow", so compare with
  // whitespace and case stripped. Blank means a row saved before FILM had a
  // Platform field — those were all Mercury.
  static resolveChannel(channel: string | null | undefined): FulfillmentChannelKind {
    const normalized = (channel ?? '').replaceAll(/\s+/g, '').toLowerCase();
    switch (normalized) {
      case 'servicenow':
        return 'servicenow';
      case 'mercury':
      case '':
        return 'mercury';
      case 'url':
        return 'url';
      default:
        return 'unknown';
    }
  }

  // Only http(s) — formId on a URL row is free text from FILM, so a
  // javascript:/data: value must never reach window.open.
  static isSafeExternalUrl(value: string | null | undefined): boolean {
    try {
      const { protocol } = new URL(value ?? '');
      return protocol === 'https:' || protocol === 'http:';
    } catch {
      return false;
    }
  }

  // formId is not unique across /formConfigDetails: FILM lets two offerings
  // point at the same Mercury form or catalog item (e.g. `lsoe-install` on
  // offerings 164029851998788 and 208634065866751). Match the offering too,
  // so the submit carries that offering's id, type and mode. Without an
  // offeringId (old links), or when it matches nothing, keep the first formId
  // match as before.
  static findFormConfig(
    list: readonly FormConfigDetails[] | null | undefined,
    formId: string,
    offeringId?: string | null
  ): FormConfigDetails | undefined {
    if (!list || !formId) return undefined;
    const byForm = list.filter((f) => String(f.formId) === String(formId));
    const exact = offeringId
      ? byForm.find((f) => String(f.offeringId) === String(offeringId))
      : undefined;
    return exact ?? byForm[0];
  }

  // Payload Builders
  static mapFormConfigToFormData(config: FormConfigDetails): FormConfigDetails {
    return {
      formId: config.formId,
      description: config.description || '',
      fulfillmentType: config.fulfillmentType ?? '',
      fulfillmentMode: config.fulfillmentMode ?? '',
      fulfillmentChannel: config.fulfillmentChannel ?? '',
      fulfillmentTitle: config.fulfillmentTitle ?? ''
    };
  }

  static mapOfferingToOfferingDetail(config: FormConfigDetails): OfferingDetails {
    return {
      offeringId: config.offeringId ?? '',
      description: config.description ?? '',
      fulfillmentMode: config.fulfillmentMode ?? '',
      offeringName: config.offeringName ?? '',
      type: config.fulfillmentType ?? '',
      displayName: config.offeringName ?? '',
      formName: config.formId ?? ''
    };
  }

  static buildFulfillmentParameters(
    formData: Partial<FulfillmentParameters>,
    currentUser: UserDetails
  ): FulfillmentParameters {

    const {
      requestedFor,
      requestedForId,
      email,
      ci,
      task,
      description,
      ...extraFields
    } = formData || {};

    return {
      requestedFor: requestedFor ?? currentUser.userName ?? '',
      requestedForId: requestedForId ?? currentUser.userId ?? '',
      email: email ?? currentUser.email ?? '',
      ci: ci ?? '',
      task: task ?? '',
      description: description ?? '',
      ...extraFields
    };
  }

  static buildSubmitPayload(
    user: UserDetails,
    formData: Partial<FulfillmentParameters>,
    config: FormConfigDetails
  ): SubmitFormPayload {

    return {
      requestDetails: {
        offeringDetails: this.mapOfferingToOfferingDetail(config),
        userDetails: user,
        formConfigDetails: this.mapFormConfigToFormData(config),
        fulfillmentParameters: this.buildFulfillmentParameters(formData, user)
      }
    };
  }


  // HTML Processing
  static processHtml(html: string): string {
    if (!html) return '';

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    this.applyBaseStyles(doc);
    this.normalizeLayout(doc);
    this.transformCheckboxes(doc);
    this.injectValidationStyles(doc);
    this.injectScript(doc);

    return doc.documentElement.outerHTML;
  }

  private static applyBaseStyles(doc: Document): void {
    const style = doc.createElement('style');
    style.textContent = `
      body { font-family: Arial, sans-serif; padding: 16px; }
      div[id$="-field"] { margin-bottom: 12px; }
    `;
    doc.head.appendChild(style);
  }

  private static injectValidationStyles(doc: Document) {
    const style = doc.createElement('style');
    // Native checkbox/radio widgets render poorly with padding — Chrome grows
    // the hit-box into the padding but skips painting :checked on first click.
    style.textContent = `
      body { font-family: 'Source Sans 3', sans-serif; }
      input:not([type="checkbox"]):not([type="radio"]),
      select,
      textarea { border-radius: 8px !important; padding: 8px !important; }
      .required-field { border: 1px solid #e53935 !important; background-color: #fff5f5; }
      .error-text { color: #e53935; font-size: 12px; margin-top: 4px; }
    `;
    doc.head.appendChild(style);
  }

  private static normalizeLayout(doc: Document) {
    const els = doc.querySelectorAll('div[class*="mercury-params-width-"]');
    els.forEach(el => {
      el.classList.forEach(cls => {
        if (cls.startsWith('mercury-params-width')) {
          el.classList.remove(cls);
          el.classList.add('mercury-params-width-12');
        }
      });
    });

    const h1 = doc.querySelector('h1');
    if (h1) (h1 as HTMLElement).style.display = 'none';

    const container = doc.querySelector('h1 + div') as HTMLElement;
    if (container) {
      container.style.height = '100vh';
      container.style.padding = '0% 7% 0% 7%';
    }
  }

  private static transformCheckboxes(doc: Document) {
    const fields = doc.querySelectorAll('div[id$="-field"]');
    fields.forEach((field: any) => {
      const checkbox = field.querySelector('input[type="checkbox"]');
      const label = field.querySelector('label');
      if (!checkbox || !label) return;

      field.style.display = 'flex';
      field.style.alignItems = 'center';
      field.style.gap = '8px';

      const wrapper = doc.createElement('div');
      wrapper.style.display = 'flex';
      wrapper.style.alignItems = 'center';
      wrapper.style.gap = '6px';

      wrapper.appendChild(checkbox);
      wrapper.appendChild(label);

      field.innerHTML = '';
      field.appendChild(wrapper);

      if (checkbox.id) label.setAttribute('for', checkbox.id);
    });
  }

  public static injectScript(doc: Document): void {
    const script = doc.createElement('script');

    script.textContent = `
  (function () {
    console.log("iFrame ready: Dynamic Options Interceptor active");

    const pendingRequests = {};
    const originalFetch = window.fetch;

    // INTERCEPT ALL FETCH CALLS
    window.fetch = function (input, init) {
      const url = typeof input === "string" ? input : input.url;

      // Intercept /options call
      if (url && url.includes("/options")) {
        console.log("[PROXY INTERCEPT]: Caught options call ->", url);

        const requestId = Date.now() + "_" + Math.random();

        let payload = {};
        try {
          payload = init?.body ? JSON.parse(init.body) : {};
        } catch(e) {
          payload = {};
        }

        const urlParts = url.split('/');
        let formName = '';
        let versionNo = '';

        const formIndex = urlParts.findIndex(p => p.toLowerCase() === 'forms' || p.toLowerCase() === 'form');
        if (formIndex !== -1 && urlParts[formIndex + 1]) {
          formName = urlParts[formIndex + 1];
        }

        // Dynamically locate the path position following 'version'
        const versionIndex = urlParts.findIndex(p => p.toLowerCase() === 'version');
        if (versionIndex !== -1 && urlParts[versionIndex + 1]) {
          versionNo = urlParts[versionIndex + 1];
        }

        // Fallbacks prevent exceptions if structural layout changes
        formName = formName || "dynamic-form";
        versionNo = versionNo || "1";

        // 2. FORWARD REQUEST PARAMS TO THE ANGULAR PARENT WINDOW
        return new Promise((resolve, reject) => {

          pendingRequests[requestId] = { resolve, reject };

          window.parent.postMessage(JSON.stringify({
            type: "options-request",
            requestId,
            formName,
            versionNo,
            payload
          }), "*");
        });
      }
      return originalFetch.apply(this, arguments);
    };

    // Receive response from angular component
    window.addEventListener("message", function (event) {

      let msg;
      try {
        msg = JSON.parse(event.data);
      } catch(e) {
        return;
      }

      // Options Response from Mulesoft
      if (msg.type === "options-response") {
        const pending = pendingRequests[msg.requestId];
        if (pending) {
          // Resolve the fetch promise with a standardized HTTP-like response block
          pending.resolve(new Response(JSON.stringify(msg.data), {
            status: 200,
            headers: { "Content-Type": "application/json" }
          }));

          delete pendingRequests[msg.requestId];
        }
      }
    });
  })();
  `;

    doc.head.appendChild(script);
  }

  public static initializeIframeScripts(iframeWindow: any, rawMulesoftString: string): void {
    if (!iframeWindow || !rawMulesoftString) return;
    const iframeDoc = iframeWindow.document;

    // Inject options proxy network interceptor
    this.injectScript(iframeDoc);

    // Parse and execute dynamic scripts provided by the Mulesoft payload
    const parser = new DOMParser();
    const originalDoc = parser.parseFromString(rawMulesoftString, 'text/html');
    const scriptsToRun = originalDoc.querySelectorAll('script');

    scriptsToRun.forEach((oldScript) => {
      const newScript = iframeDoc.createElement('script');
      if (oldScript.src) {
        newScript.src = oldScript.src;
      } else {
        newScript.textContent = oldScript.textContent;
      }
      iframeDoc.head.appendChild(newScript);
    });

    // Mercury's checkbox_updated is a 3-state machine (indeterminate → false → true)
    // keyed on data-state. Missing data-state defaults to indeterminate, so first click
    // lands on "false" (no tick) and users have to click twice. Preseed uninitialized,
    // unchecked checkboxes to "false" so first click goes false → true.
    setTimeout(() => {
      iframeDoc.querySelectorAll('input[type="checkbox"]').forEach((cb: HTMLInputElement) => {
        if (!cb.getAttribute('data-state') && !cb.checked) {
          cb.indeterminate = false;
          cb.setAttribute('data-state', 'false');
        }
      });
    }, 100);
  }

  // Extracts data from the iframe context and returns a cleanly typed key-value.
  public static extractPerfectlyTypedData(iframeWindow: any): Record<string, unknown> {
    if (!iframeWindow) return {};
    const iframeDoc = iframeWindow.document;
    const finalPayload: Record<string, unknown> = {};

    // Use Mercury's native typed state if active
    if (iframeWindow.form_values) {
      return { ...iframeWindow.form_values };
    }

    // Dynamic Element parsing scanner fallback
    const dynamicElements = iframeDoc.querySelectorAll('[data-value]');
    dynamicElements.forEach((el: any) => {
      try {
        const rawAttr = el.getAttribute('data-value');
        if (!rawAttr) return;
        const parsedData = JSON.parse(rawAttr);
        if (typeof parsedData === 'object' && parsedData !== null) {
          Object.assign(finalPayload, parsedData);
        } else {
          const key = el.name || el.id || el.getAttribute('data-field-name');
          if (key) finalPayload[key] = parsedData;
        }
      } catch {
        const key = el.name || el.id || el.getAttribute('data-field-name');
        if (key) finalPayload[key] = el.getAttribute('data-value');
      }
    });

    // Native standard form elements data scanner fallback
    const allInputs = iframeDoc.querySelectorAll('input, select, textarea') as NodeListOf<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>;
    allInputs.forEach(input => {
      const key = input.name || input.id;
      if (!key || finalPayload[key] !== undefined) return;

      const val = input.value;
      if (input instanceof HTMLInputElement && input.type === 'checkbox') {
        finalPayload[key] = input.checked;
      } else if (input instanceof HTMLInputElement && input.type === 'radio') {
        if (input.checked) finalPayload[key] = val === 'on' ? true : (val === 'off' ? false : val);
      } else if (input.type === 'number' || input.type === 'range') {
        finalPayload[key] = val !== '' ? Number(val) : null;
      } else {
        const isPureNumericString = /^\d+$/.test(val) && (val.length === 1 || !val.startsWith('0'));
        if (isPureNumericString) {
          finalPayload[key] = Number(val);
        } else if (val === 'true' || val === 'on') {
          finalPayload[key] = true;
        } else if (val === 'false' || val === 'off') {
          finalPayload[key] = false;
        } else {
          finalPayload[key] = val;
        }
      }
    });

    return finalPayload;
  }
}
